const CACHE_PREFIX = "recital-master-artwork-v1";
const objectUrls = new Map<string, string>();
const pending = new Map<string, Promise<string>>();

function hash(value: string): string {
  let result = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    result = Math.imul(result ^ value.charCodeAt(index), 0x01000193);
  }
  return (result >>> 0).toString(16);
}

function cacheName(uid: string): string {
  return `${CACHE_PREFIX}-${hash(uid)}`;
}

function cacheRequest(uid: string, objectKey: string): Request {
  return new Request(
    `https://artwork-cache.recitalmaster.invalid/${hash(uid)}/${encodeURIComponent(objectKey)}`
  );
}

async function responseUri(registryKey: string, response: Response): Promise<string> {
  const existing = objectUrls.get(registryKey);
  if (existing) return existing;
  const uri = URL.createObjectURL(await response.blob());
  objectUrls.set(registryKey, uri);
  return uri;
}

export async function persistArtwork(
  uid: string,
  objectKey: string,
  remoteUrl: string
): Promise<string> {
  if (!("caches" in globalThis)) return remoteUrl;
  const registryKey = `${uid}:${objectKey}`;
  const known = objectUrls.get(registryKey);
  if (known) return known;
  const existing = pending.get(registryKey);
  if (existing) return existing;

  const load = (async () => {
    try {
      const cache = await caches.open(cacheName(uid));
      const request = cacheRequest(uid, objectKey);
      const cached = await cache.match(request);
      if (cached) return responseUri(registryKey, cached);

      const response = await fetch(remoteUrl);
      if (!response.ok) throw new Error(`Artwork request failed (${response.status}).`);
      await cache.put(request, response.clone());
      return responseUri(registryKey, response);
    } finally {
      pending.delete(registryKey);
    }
  })();
  pending.set(registryKey, load);
  return load;
}

export async function clearPersistentArtwork(uid: string): Promise<void> {
  if ("caches" in globalThis) await caches.delete(cacheName(uid));
  for (const [key, uri] of objectUrls) {
    if (!key.startsWith(`${uid}:`)) continue;
    URL.revokeObjectURL(uri);
    objectUrls.delete(key);
  }
}
